import { useEffect, useMemo, useState } from "react";

import moment from "moment";
import { Printer } from "lucide-react";

// Interface
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";

import { AlertV4, ButtonV4, ChipV4, FieldV4, SelectV4 } from "../common/ui";
import ModalAdviceTemplateV4 from "./ModalAdviceTemplateV4";
import ModalCancelOrderAppointmentV4 from "./ModalCancelOrderAppointmentV4";
import OrderAppointmentAdvanceV4, { type AdvancePanel } from "./OrderAppointmentAdvanceV4";
import {
  ACTIONS,
  BTN_ACTS,
  SEQUENCE_NAME,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/OrderAppointmentV4";
import type { OrderMasterOptions, SetEntry } from "../common/sequence/OrderSheetV4";
import { blocksFromDsb, dateKeyOf, type Block } from "../appointment/appointmentModelV4";
import type { PickItem, SheetCategory } from "./ordersModelV4";
import {
  APPOINTMENT_KINDS,
  CANCEL_REASONS,
  appendAdvice,
  cellsOf,
  dayDateText,
  draftProblems,
  emptyDraft,
  kindOf,
  openRowText,
  rangeText,
  sortRows,
  statusToneOf,
  timeOptions,
  toAdviceGroup,
  toAppointmentRow,
  toEntry,
  type AdviceGroup,
  type AdviceItem,
  type AdvanceOrder,
  type AppointmentDraft,
  type AppointmentKind,
  type AppointmentRow,
  type ViewType,
} from "./orderAppointmentModelV4";

// Types
export type AdvanceSearch = {
  query: string;
  category: SheetCategory | "all";
  /** ผลค้นหาเมื่อช่องค้นหาของนัดหมายเป็นช่องที่ใช้อยู่ */
  results: PickItem[];
  isSearching: boolean;
  orderSets: { id: number; name: string }[];
  favourites: PickItem[];
  setEntriesOf: (id: number) => SetEntry[];
  onChangeQuery: (query: string) => void;
  onChangeCategory: (category: SheetCategory | "all") => void;
  onOpenSets: () => void;
};

type CardOrderAppointmentV4Props = {
  OrderAppointmentV4Sequence?: State["OrderAppointmentV4Sequence"];
  buttonLoadCheck?: Record<string, string>;
  errorMessage?: Record<string, unknown>;
  masterOptions?: OrderMasterOptions;
  runSequence: RunSequence;
  setProp?: SetProp;
  encounterId: number;
  patient: { hn: string; name: string; phone: string };
  divisionName: string;
  eligibleProviderIds?: Set<number>;
  search: AdvanceSearch;
  onPrint: (appointment: Record<string, any>, language: "TH" | "EN") => void;
  onGoAppointments?: () => void;
  onChanged?: () => void;
};

// Const
const VIEWS: { key: ViewType; label: string }[] = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "day", label: "Day" },
];

const DOW = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

/** error ที่แสดงเป็นแถบบนการ์ด (ยกเลิกนัดแสดงใน modal) */
const ERROR_KEYS = [BTN_ACTS.LOAD_APPOINTMENTS, BTN_ACTS.LOAD_SLOTS, BTN_ACTS.SAVE];

const ERROR_TEXTS: Record<string, string> = {
  [BTN_ACTS.LOAD_APPOINTMENTS]: "โหลดรายการนัดไม่สำเร็จ: ",
  [BTN_ACTS.LOAD_SLOTS]: "โหลดตารางออกตรวจไม่สำเร็จ: ",
  [BTN_ACTS.SAVE]: "บันทึกนัดหมายไม่สำเร็จ: ",
};

const ALL = "all";

const CardOrderAppointmentV4 = (props: CardOrderAppointmentV4Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState<AppointmentDraft | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [problems, setProblems] = useState<string[]>([]);
  const [message, setMessage] = useState<{ text: string } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [adviceOpen, setAdviceOpen] = useState(false);
  const [language, setLanguage] = useState<"TH" | "EN">("TH");
  const [view, setView] = useState<ViewType>("month");
  const [anchor, setAnchor] = useState(() => moment().startOf("day"));
  const [specialty, setSpecialty] = useState(ALL);
  const [providerId, setProviderId] = useState(ALL);
  const [editingKey, setEditingKey] = useState("");
  const [panel, setPanel] = useState<AdvancePanel>("");
  const [customItems, setCustomItems] = useState<AdviceItem[]>([]);
  const [customGroups, setCustomGroups] = useState<AdviceGroup[]>([]);

  // Memo
  const seq = props.OrderAppointmentV4Sequence;

  const cells = useMemo(() => cellsOf(view, anchor), [view, anchor]);

  const typeOptions = props.masterOptions?.patientAppType ?? [];

  const rows = useMemo(
    () => sortRows((seq?.appointments ?? []).map((item) => toAppointmentRow(item, typeOptions))),
    [seq?.appointments, typeOptions],
  );

  const blocks = useMemo(
    () =>
      blocksFromDsb(seq?.dsb, seq?.providers as any).filter(
        (block) => !props.eligibleProviderIds || props.eligibleProviderIds.has(block.providerId),
      ),
    [seq?.dsb, seq?.providers, props.eligibleProviderIds],
  );

  const specialtyOptions = useMemo(
    () => [
      { value: ALL, text: "ทั้งหมด" },
      ...[...new Set(blocks.map((block) => String(block.raw.specialty ?? "")).filter(Boolean))].map(
        (text) => ({ value: text, text }),
      ),
    ],
    [blocks],
  );

  const providerOptions = useMemo(() => {
    const seen = new Map<number, string>();

    for (const block of blocks) {
      if (specialty === ALL || block.raw.specialty === specialty) {
        seen.set(block.providerId, block.provider);
      }
    }

    return [
      { value: ALL, text: "ทุกผู้ให้บริการ" },
      ...[...seen].map(([id, name]) => ({ value: String(id), text: name })),
    ];
  }, [blocks, specialty]);

  const shownBlocks = blocks.filter(
    (block) =>
      (specialty === ALL || block.raw.specialty === specialty) &&
      (providerId === ALL || String(block.providerId) === providerId),
  );

  const legend = [...new Map(shownBlocks.map((block) => [block.providerId, block])).values()];

  const editingRow = rows.find((row) => row.id === draft?.id);

  const isReadOnly = !!editingRow?.isCancelled || !!editingRow?.isFinished;

  const readOnlyText = editingRow?.isCancelled
    ? "นัดนี้ถูกยกเลิกแล้ว"
    : "นัดนี้เปิด Encounter แล้ว";

  const isSaving = props.buttonLoadCheck?.[BTN_ACTS.SAVE] === BLC_STATUS.LOADING;

  const isCanceling = props.buttonLoadCheck?.[BTN_ACTS.CANCEL] === BLC_STATUS.LOADING;

  const adviceItems: AdviceItem[] = [
    ...(seq?.noteTemplates ?? []).map((item) => ({
      key: `n${item.id}`,
      text: item.text,
      isCustom: false,
    })),
    ...customItems,
  ];

  const adviceGroups = [...(seq?.noteGroups ?? []).map(toAdviceGroup), ...customGroups];

  const closeText = draft?.id ? "✕ ปิดโดยไม่บันทึก" : "✕ ยกเลิกการสร้างนัดนี้";

  // Effect
  useEffect(() => {
    props.runSequence({ sequence: SEQUENCE_NAME, restart: true, encounterId: props.encounterId });
  }, [props.encounterId]);

  useEffect(() => {
    if (!draft || step !== 1) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD_SLOTS,
      start: dateKeyOf(cells[0]),
      end: dateKeyOf(cells[cells.length - 1]),
    });
  }, [!!draft, step, cells]);

  useEffect(() => {
    if (draft?.block && !seq?.slotDetails?.[draft.block.dsbId]) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.LOAD_SLOT_DETAIL,
        dsbId: draft.block.dsbId,
      });
    }
  }, [draft?.block?.dsbId]);

  // Handler
  const handleChangeSpecialty = (value: string) => {
    setSpecialty(value);
    setProviderId(ALL);
  };

  const handleGoStep1 = () => {
    setStep(1);
  };

  const handleGoStep2 = () => {
    setStep(2);
  };

  const handleOpenCancel = () => {
    setCancelOpen(true);
  };

  const handleOpenNew = (kind: AppointmentKind) => {
    setMenuOpen(false);
    setDraft(emptyDraft(kind));
    setStep(1);
    setProblems([]);
    setMessage(null);
    setAnchor(moment().startOf("day"));
  };

  const handleOpenRow = (row: AppointmentRow) => {
    const info = row.raw.display_info;
    const at = moment(row.raw.estimated_at_iso);

    setDraft({
      id: row.id,
      kind: row.kind,
      block: {
        dsbId: Number(row.raw.division_service_block),
        providerId: Number(info?.provider),
        provider: row.provider,
        date: at.format("YYYY-MM-DD"),
        start: info ? moment(info.start_datetime_iso).format("HH:mm") : row.time,
        end: info ? moment(info.end_datetime_iso).format("HH:mm") : row.time,
        order: 0,
        colorIndex: 1,
        items: [],
        raw: { specialty: row.raw.doctor_specialties?.[0]?.name ?? "" },
      },
      time: row.time,
      duration: String(row.duration),
      type: String(row.raw.type ?? ""),
      appointmentFor: row.purpose,
      advice: row.raw.order_note ?? "",
      orders: row.orders,
    });
    setStep(2);
    setProblems([]);
    setMessage(null);
    setAnchor(at.clone().startOf("day"));
  };

  const handleClose = () => {
    setDraft(null);
    setProblems([]);
    setEditingKey("");
    setPanel("");
    props.search.onChangeQuery("");
  };

  const handleChange = (data: Partial<AppointmentDraft>) => {
    setDraft(draft ? { ...draft, ...data } : draft);
  };

  const handlePickBlock = (block: Block) => {
    handleChange({
      block,
      time: block.start,
      duration: String(Number(block.raw.doctor_dsb_slot_length) || ""),
    });
    setStep(2);
  };

  const handleMove = (step: 1 | -1 | 0) => {
    if (step === 0) {
      setAnchor(moment().startOf("day"));

      return;
    }

    const unit = view === "month" ? "months" : view === "week" ? "weeks" : "days";

    setAnchor(anchor.clone().add(step, unit));
  };

  const handleSave = () => {
    if (!draft || !draft.block) {
      return;
    }

    const found = draftProblems(draft);
    if (props.eligibleProviderIds && !props.eligibleProviderIds.has(draft.block.providerId)) {
      found.push("กรุณาเลือกผู้ให้บริการของ OA นี้ใหม่");
    }

    setProblems(found);

    if (found.length > 0) {
      return;
    }

    const block = draft.block;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SAVE,
      form: {
        id: draft.id,
        kind: draft.kind,
        dsbId: block.dsbId,
        date: block.date,
        time: draft.time,
        duration: Number(draft.duration),
        type: draft.type,
        appointmentFor: draft.appointmentFor,
        advice: draft.advice,
        orders: draft.orders,
      },
      onDone: (result) => {
        const when = `${dayDateText(moment(block.date))} ${draft.time}`;

        setMessage({
          text: [
            draft.id ? `บันทึกการแก้ไขนัดหมาย ${draft.id} แล้ว` : `สร้างนัดหมาย ${result.id} แล้ว`,
            when,
            block.provider,
            `คำสั่งล่วงหน้า ${draft.orders.length} รายการ`,
          ].join(" · "),
        });
        handleClose();
        props.onChanged?.();
      },
    });
  };

  const handleCloseCancel = () => {
    setCancelOpen(false);
    handleCloseError(BTN_ACTS.CANCEL);
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  const handleConfirmCancel = (reason: (typeof CANCEL_REASONS)[number]) => {
    if (!draft?.id) {
      return;
    }

    const id = draft.id;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.CANCEL,
      id,
      status: reason.status,
      reason: reason.text,
      onDone: () => {
        setCancelOpen(false);
        setMessage({ text: `นัดหมาย ${id} · ยกเลิกนัด (${reason.text}) แล้ว` });
        props.onChanged?.();
        handleClose();
      },
    });
  };

  const handleAddOrders = (picks: SetEntry[]) => {
    if (!draft) {
      return;
    }

    const keys = new Set(draft.orders.map((order) => order.key));
    const added = picks.filter((pick) => !keys.has(pick.key));

    handleChange({ orders: [...draft.orders, ...added.map((pick) => ({ ...pick, note: "" }))] });
  };

  const handleAddPick = (pick: PickItem) => {
    handleAddOrders([toEntry(pick)]);
  };

  const handleEditOrder = (key: string, data: Partial<AdvanceOrder>) => {
    handleChange({
      orders: (draft?.orders ?? []).map((order) =>
        order.key === key ? { ...order, ...data } : order,
      ),
    });
  };

  const handleRemoveOrder = (key: string) => {
    handleChange({ orders: (draft?.orders ?? []).filter((order) => order.key !== key) });
  };

  const handleTogglePanel = (value: "sets" | "favs") => {
    if (value === "sets" && panel !== "sets") {
      props.search.onOpenSets();
    }

    setPanel(panel === value ? "" : value);
  };

  const handleOpenAdvice = () => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_NOTE_TEMPLATES });
    setAdviceOpen(true);
  };

  const handleAddAdvice = (texts: string[]) => {
    handleChange({ advice: appendAdvice(draft?.advice ?? "", texts) });
    setAdviceOpen(false);
  };

  const handleCloseAdvice = () => {
    setAdviceOpen(false);
  };

  const handleAddAdviceItem = (item: AdviceItem) => {
    setCustomItems([...customItems, item]);
  };

  const handleRemoveAdviceItem = (key: string) => {
    setCustomItems(customItems.filter((item) => item.key !== key));
  };

  const handleAddAdviceGroup = (group: AdviceGroup) => {
    setCustomGroups([...customGroups, group]);
  };

  const handleRemoveAdviceGroup = (key: string) => {
    setCustomGroups(customGroups.filter((group) => group.key !== key));
  };

  // Render
  const renderCalendar = () => {
    const today = moment().startOf("day");
    const atStart =
      !cells[0] || !moment(cells[0]).isAfter(today, view === "month" ? "month" : "day");
    const canGoBack = view === "month" ? anchor.isAfter(today, "month") : !atStart;

    return (
      <div className="hv4-ord-ap-step1">
        <div className="hv4-ord-ap-filters">
          <FieldV4 label="แผนก *" htmlFor="hv4_ord_ap_div">
            <SelectV4
              id="hv4_ord_ap_div"
              options={[{ value: "div", text: editingRow?.division || props.divisionName || "-" }]}
              value="div"
              disabled
              onChange={() => undefined}
            />
          </FieldV4>
          <FieldV4 label="ความเชี่ยวชาญ" htmlFor="hv4_ord_ap_spec">
            <SelectV4
              id="hv4_ord_ap_spec"
              options={specialtyOptions}
              value={specialty}
              onChange={handleChangeSpecialty}
            />
          </FieldV4>
          <FieldV4 label="ผู้ให้บริการ" htmlFor="hv4_ord_ap_prov">
            <SelectV4
              id="hv4_ord_ap_prov"
              options={providerOptions}
              value={providerId}
              onChange={setProviderId}
            />
          </FieldV4>
        </div>

        <div className="hv4-ord-ap-cal">
          <div className="hv4-ord-ap-bar">
            <ButtonV4 variant="ghost" onClick={() => handleMove(0)}>
              Today
            </ButtonV4>
            <div className="hv4-ord-ap-nav">
              <ButtonV4
                variant="ghost"
                aria-label="ก่อนหน้า"
                disabled={!canGoBack}
                onClick={() => handleMove(-1)}
              >
                ‹
              </ButtonV4>
              <ButtonV4 variant="ghost" aria-label="ถัดไป" onClick={() => handleMove(1)}>
                ›
              </ButtonV4>
            </div>
            <b className="hv4-ord-ap-range">{rangeText(view, anchor, cells)}</b>
            <div className="hv4-ord-ap-seg" role="group" aria-label="มุมมอง">
              {VIEWS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  aria-pressed={view === item.key}
                  onClick={() => setView(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hv4-ord-ap-legend">
            {legend.map((block) => (
              <span key={block.providerId}>
                <i className={`hv4-ord-ap-swatch hv4-appt-prov-${block.colorIndex}`} />
                {block.provider}
              </span>
            ))}
            <span>
              <i className="hv4-appt-dot" /> มีนัดแล้วในคาบ
            </span>
            <span>
              <i className="hv4-ord-ap-swatch hv4-ord-ap-swatch--past" /> ผ่านมาแล้ว / นอกเดือน
            </span>
          </div>

          <div className={`hv4-ord-ap-dow hv4-ord-ap-dow--${view}`}>
            {(view === "day" ? cells : cells.slice(0, 7)).map((date) => (
              <span key={dateKeyOf(date)}>
                {DOW[(moment(date).isoWeekday() + 6) % 7]}
                {view !== "month" && ` ${moment(date).date()}`}
              </span>
            ))}
          </div>

          <div className={`hv4-ord-ap-grid hv4-ord-ap-grid--${view}`}>
            {cells.map((date) => {
              const at = moment(date);
              const key = dateKeyOf(date);
              const dayBlocks = shownBlocks
                .filter((block) => block.date === key)
                .sort((a, b) => a.order - b.order || a.start.localeCompare(b.start));
              const isOut = view === "month" && !at.isSame(anchor, "month");
              const isPast = at.isBefore(today, "day");
              const isWeekend = at.isoWeekday() >= 6;

              return (
                <div
                  key={key}
                  className={[
                    "hv4-ord-ap-cell",
                    isOut && "hv4-ord-ap-cell--out",
                    isWeekend && !isOut && "hv4-ord-ap-cell--weekend",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="hv4-ord-ap-num">
                    {at.isSame(today, "day") ? (
                      <b className="hv4-ord-ap-today">{at.date()}</b>
                    ) : (
                      at.date()
                    )}
                  </span>
                  {dayBlocks.map((block) => {
                    const detail = seq?.slotDetails?.[block.dsbId];
                    const isFull = !!block.raw.doctor_dsb_full;
                    const isPicked = draft?.block?.dsbId === block.dsbId;

                    return (
                      <button
                        key={block.dsbId}
                        type="button"
                        disabled={isPast || isFull || isReadOnly}
                        aria-pressed={isPicked}
                        title={`นัดแล้ว ${block.items.length}${
                          detail?.capacity ? ` (${detail.capacity})` : ""
                        }`}
                        className={`hv4-ord-ap-slot hv4-appt-prov-${block.colorIndex}${
                          isPicked ? " hv4-ord-ap-slot--on" : ""
                        }`}
                        onMouseEnter={() =>
                          !detail &&
                          props.runSequence({
                            sequence: SEQUENCE_NAME,
                            action: ACTIONS.LOAD_SLOT_DETAIL,
                            dsbId: block.dsbId,
                          })
                        }
                        onClick={() => handlePickBlock(block)}
                      >
                        {block.items.length > 0 && (
                          <i className="hv4-appt-dot" aria-label="มีนัด" />
                        )}
                        {view === "day" ? (
                          <span>
                            <b>
                              {block.start} - {block.end} {block.provider}
                            </b>
                            {block.raw.specialty ? ` · ${block.raw.specialty}` : ""} · นัดแล้ว{" "}
                            {block.items.length}
                            {detail?.capacity ? ` (${detail.capacity})` : ""}
                          </span>
                        ) : (
                          <span>
                            {block.start} - {block.end}{" "}
                            {block.provider
                              .replace(block.raw.doctor_provider_last_name ?? "", "")
                              .trim()}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {view !== "month" && dayBlocks.length === 0 && (
                    <span className="hv4-ord-ap-none">ไม่มีคาบออกตรวจ</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    if (!draft?.block) {
      return null;
    }

    const block = draft.block;
    const others = ((seq?.slotDetails?.[block.dsbId]?.service_appointments ?? []) as any[]).filter(
      (item) => item.id !== draft.id && ![5, 6].includes(Number(item.status)),
    );

    return (
      <div className="hv4-ord-ap-step2">
        <b className="hv4-ord-ap-sub">รายชื่อผู้ป่วยอื่น ๆ ในคาบเดียวกัน</b>
        <table className="hv4-ord-ap-table">
          <thead>
            <tr>
              <th>ชื่อผู้รับบริการ</th>
              <th>เวลาเริ่ม</th>
              <th>ระยะเวลา</th>
              <th>ผู้ทำนัดหมาย</th>
              <th>วันเวลาที่ทำนัด</th>
              <th>นัดหมายเพื่อ</th>
            </tr>
          </thead>
          <tbody>
            {others.map((item) => (
              <tr key={item.id}>
                <td>
                  {[item.patient_pre_name, item.patient_first_name, item.patient_last_name]
                    .filter(Boolean)
                    .join(" ")}
                </td>
                <td>{moment(item.estimated_at_iso).format("HH:mm")}</td>
                <td>{item.estimated_duration} นาที</td>
                <td>{item.edit_user_employee_name}</td>
                <td>{formatISOtoBEDateTime(item.created).dateTimeShort}</td>
                <td>{item.extra?.appointmentFor ?? ""}</td>
              </tr>
            ))}
            {others.length === 0 && (
              <tr>
                <td colSpan={6} className="hv4-ord-ap-empty">
                  ยังไม่มีผู้ป่วยอื่นในคาบนี้
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="hv4-ord-ap-info">
          <div>
            <b>HN:</b> {props.patient.hn} <b>ชื่อ-สกุล:</b> {props.patient.name} <b>เบอร์โทร:</b>{" "}
            {props.patient.phone || "-"}
          </div>
          <div>
            <b>ผู้ให้บริการ:</b> {block.provider}
            {block.raw.specialty ? ` (${block.raw.specialty})` : ""} · {block.start} - {block.end}
          </div>
        </div>

        <div className="hv4-ord-ap-fields">
          <FieldV4 label="แผนก" htmlFor="hv4_ord_ap_d">
            <input
              id="hv4_ord_ap_d"
              className="hv4-input"
              value={editingRow?.division || props.divisionName}
              readOnly
            />
          </FieldV4>
          <FieldV4 label="วันที่นัดหมาย" htmlFor="hv4_ord_ap_date">
            <input
              id="hv4_ord_ap_date"
              className="hv4-input"
              value={dayDateText(moment(block.date))}
              readOnly
            />
          </FieldV4>
          <FieldV4 label="เวลานัดหมาย *" htmlFor="hv4_ord_ap_time">
            <SelectV4
              id="hv4_ord_ap_time"
              options={timeOptions(block.start, block.end).map((time) => ({
                value: time,
                text: time,
              }))}
              value={draft.time}
              disabled={isReadOnly}
              onChange={(time) => handleChange({ time })}
            />
          </FieldV4>
          <FieldV4 label="ระยะเวลา (นาที) *" htmlFor="hv4_ord_ap_dur">
            <input
              id="hv4_ord_ap_dur"
              className="hv4-input"
              type="number"
              min={1}
              value={draft.duration}
              disabled={isReadOnly}
              onChange={(e) => handleChange({ duration: e.target.value })}
            />
          </FieldV4>
          <FieldV4 label="ประเภทการนัดหมาย *" htmlFor="hv4_ord_ap_type">
            <SelectV4
              id="hv4_ord_ap_type"
              options={typeOptions}
              placeholder="เลือก"
              value={draft.type}
              disabled={isReadOnly}
              onChange={(type) => handleChange({ type })}
            />
          </FieldV4>
          <FieldV4 label="นัดเพื่อ *" htmlFor="hv4_ord_ap_for">
            <SelectV4
              id="hv4_ord_ap_for"
              options={props.masterOptions?.appointmentForNote ?? []}
              placeholder="เลือก"
              value={draft.appointmentFor}
              disabled={isReadOnly}
              onChange={(appointmentFor) => handleChange({ appointmentFor })}
            />
          </FieldV4>
        </div>

        {draft.kind === "general" && (
          <div className="hv4-ord-ap-advice">
            <div className="hv4-ord-ap-row">
              <b className="hv4-ord-ap-sub">คำแนะนำในการปฏิบัติตัวเพิ่มเติม</b>
              <ButtonV4
                className="hv4-ord-ap-blue"
                disabled={isReadOnly}
                onClick={handleOpenAdvice}
              >
                + เพิ่มคำแนะนำ
              </ButtonV4>
            </div>
            <textarea
              className="hv4-input"
              aria-label="คำแนะนำในการปฏิบัติตัวเพิ่มเติม"
              rows={3}
              value={draft.advice}
              readOnly={isReadOnly}
              onChange={(e) => handleChange({ advice: e.target.value })}
            />
          </div>
        )}

        {renderAdvanceOrders()}
      </div>
    );
  };

  const renderAdvanceOrders = () =>
    draft && (
      <OrderAppointmentAdvanceV4
        // data
        orders={draft.orders}
        search={props.search}
        readOnly={isReadOnly}
        panel={panel}
        editingKey={editingKey}
        // callback
        onAddPick={handleAddPick}
        onAddOrders={handleAddOrders}
        onEditOrder={handleEditOrder}
        onRemoveOrder={handleRemoveOrder}
        onTogglePanel={handleTogglePanel}
        onChangeEditing={setEditingKey}
      />
    );

  const renderForm = () => {
    if (!draft) {
      return null;
    }

    const canSave =
      !isReadOnly &&
      !isSaving &&
      (!props.eligibleProviderIds ||
        (!!draft.block && props.eligibleProviderIds.has(draft.block.providerId)));

    return (
      <div className="hv4-ord-ap-form">
        <div className="hv4-ord-ap-steps">
          <button
            type="button"
            className="hv4-ord-ap-step"
            aria-pressed={step === 1}
            disabled={isReadOnly}
            onClick={handleGoStep1}
          >
            1 · เลือกวันเวลาออกตรวจ
          </button>
          <button
            type="button"
            className="hv4-ord-ap-step"
            aria-pressed={step === 2}
            disabled={!draft.block}
            onClick={handleGoStep2}
          >
            2 · รายละเอียดนัดหมาย
          </button>
          <ButtonV4 variant="danger-ghost" className="hv4-ord-ap-close" onClick={handleClose}>
            {closeText}
          </ButtonV4>
        </div>

        {step === 1 ? renderCalendar() : renderDetail()}

        {problems.length > 0 && (
          <AlertV4 tone="danger">กรุณากรอก/แก้ไข: {problems.join(", ")}</AlertV4>
        )}

        <div className="hv4-ord-ap-foot">
          <ButtonV4 variant="danger-ghost" onClick={handleClose}>
            {closeText}
          </ButtonV4>
          <span className="hv4-ord-ap-grow" />
          <span title={isReadOnly ? readOnlyText : undefined}>
            <ButtonV4 className="hv4-ord-appt-pink" disabled={!canSave} onClick={handleSave}>
              {draft.id ? "บันทึกการแก้ไข" : "บันทึก"}
            </ButtonV4>
          </span>
          <ButtonV4
            variant="danger-ghost"
            disabled={!draft.id || isReadOnly}
            onClick={handleOpenCancel}
          >
            ยกเลิกนัด
          </ButtonV4>
          <i className="hv4-ord-ap-sep" />
          <label className="hv4-label" htmlFor="hv4_ord_ap_lang">
            ภาษา
          </label>
          <SelectV4
            id="hv4_ord_ap_lang"
            className="hv4-ord-ap-lang"
            size="sm"
            options={[
              { value: "TH", text: "TH" },
              { value: "EN", text: "EN" },
            ]}
            value={language}
            onChange={(value) => setLanguage(value as "TH" | "EN")}
          />
          <ButtonV4
            variant="ghost"
            disabled={!editingRow}
            onClick={() => editingRow && props.onPrint(editingRow.raw, language)}
          >
            <Printer size={14} /> พิมพ์ใบนัด
          </ButtonV4>
        </div>
      </div>
    );
  };

  return (
    <section aria-label="นัดหมายครั้งถัดไป" className="hv4-ord-appt">
      <div className="hv4-ord-appt-head">
        <span className="hv4-ord-appt-title">นัดหมายครั้งถัดไป</span>
        <ChipV4 size="sm" tone="grey">
          {rows.length} นัด
        </ChipV4>
        {!!draft && (
          <ChipV4 size="sm" tone={kindOf(draft.kind).tone}>
            {kindOf(draft.kind).text}
          </ChipV4>
        )}
        {!!editingRow && (
          <ChipV4 size="sm" tone={statusToneOf(editingRow)}>
            {editingRow.statusText} · เลขนัด {editingRow.id}
          </ChipV4>
        )}
        <div className="hv4-ord-ap-menuwrap">
          <ButtonV4
            className="hv4-ord-appt-pink"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            + สร้างนัดหมาย ▼
          </ButtonV4>
          {menuOpen && (
            <div className="hv4-ord-ap-menu" role="menu" aria-label="เลือกประเภทนัดหมาย">
              <div className="hv4-ord-ap-menu-title">เลือกประเภทนัดหมาย</div>
              {APPOINTMENT_KINDS.map((item) => (
                <button
                  key={item.kind}
                  type="button"
                  role="menuitem"
                  className={`hv4-ord-ap-menu-item hv4-ord-ap-menu-item--${item.kind}`}
                  onClick={() => handleOpenNew(item.kind)}
                >
                  {item.text}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {ERROR_KEYS.filter((key) => !!props.errorMessage?.[key]).map((key) => (
        <AlertV4
          key={key}
          tone="danger"
          error={props.errorMessage?.[key]}
          onClose={() => handleCloseError(key)}
        >
          {ERROR_TEXTS[key]}
        </AlertV4>
      ))}

      {!!message && (
        <div className="hv4-ord-appt-msg">
          {message.text}
          {props.onGoAppointments && (
            <button type="button" className="hv4-ord-appt-link" onClick={props.onGoAppointments}>
              ไปหน้านัดหมาย →
            </button>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <table className="hv4-ord-ap-table hv4-ord-ap-list">
          <thead>
            <tr>
              <th>เลขนัด</th>
              <th>ประเภท</th>
              <th>วันที่ / เวลา</th>
              <th>ผู้ให้บริการ / แผนก</th>
              <th>นัดเพื่อ</th>
              <th>คำสั่งล่วงหน้า</th>
              <th>สถานะ</th>
              <th aria-label="จัดการ" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isEditing = draft?.id === row.id;

              return (
                <tr
                  key={row.id}
                  className={[
                    row.isCancelled && "hv4-ord-ap-list--cancelled",
                    isEditing && "hv4-ord-ap-list--editing",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <td>
                    <b>{row.id}</b>
                  </td>
                  <td>
                    <ChipV4 size="sm" tone={kindOf(row.kind).tone}>
                      {kindOf(row.kind).text}
                    </ChipV4>
                  </td>
                  <td>
                    <b>{row.dateText}</b>
                    <div className="hv4-ord-ap-hint">
                      {row.time} · {row.duration} นาที
                    </div>
                  </td>
                  <td>
                    {row.provider}
                    <div className="hv4-ord-ap-hint">{row.division}</div>
                  </td>
                  <td>
                    {row.purpose}
                    <div className="hv4-ord-ap-hint">{row.typeText}</div>
                  </td>
                  <td>
                    {row.orders.length > 0 ? (
                      <span className="hv4-ord-ap-tipwrap" tabIndex={0}>
                        {row.orders.length} รายการ
                        <span role="tooltip" className="hv4-ord-ap-tip">
                          {row.orders.map((order) => (
                            <span key={order.key}>{order.name}</span>
                          ))}
                        </span>
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <ChipV4 size="sm" tone={statusToneOf(row)}>
                      {row.statusText}
                    </ChipV4>
                  </td>
                  <td>
                    <ButtonV4
                      variant="ghost"
                      size="sm"
                      disabled={isEditing}
                      onClick={() => handleOpenRow(row)}
                    >
                      {openRowText(row, isEditing)}
                    </ButtonV4>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {renderForm()}

      {cancelOpen && !!draft?.id && (
        <ModalCancelOrderAppointmentV4
          // data
          title={`นัดหมาย ${draft.id} · ${draft.block ? dayDateText(moment(draft.block.date)) : ""} ${draft.time}`}
          busy={isCanceling}
          error={props.errorMessage?.[BTN_ACTS.CANCEL]}
          // callback
          onClose={handleCloseCancel}
          onConfirm={handleConfirmCancel}
        />
      )}

      {adviceOpen && (
        <ModalAdviceTemplateV4
          items={adviceItems}
          groups={adviceGroups}
          onAddItem={handleAddAdviceItem}
          onRemoveItem={handleRemoveAdviceItem}
          onAddGroup={handleAddAdviceGroup}
          onRemoveGroup={handleRemoveAdviceGroup}
          onConfirm={handleAddAdvice}
          onClose={handleCloseAdvice}
        />
      )}
    </section>
  );
};

CardOrderAppointmentV4.displayName = "CardOrderAppointmentV4";

export default CardOrderAppointmentV4;
